import { defineStore } from 'pinia'
import { skills, services } from '~/data/profile'
import type { MenuItem, Project } from '~/types/portfolio'

export type PortfolioSectionId = 'hero' | 'services' | 'projects' | 'contact'

const defaultSections: PortfolioSectionId[] = ['hero', 'services', 'projects', 'contact']
const sectionAliases: Record<PortfolioSectionId, string[]> = {
  hero: ['hero', 'home', 'index', 'dashboard', 'main'],
  services: ['services', 'service'],
  projects: ['projects', 'project', 'portfolio', 'works', 'work'],
  contact: ['contact', 'contacts', 'contact-us', 'contact_us']
}

function isActive(value: MenuItem['active']) {
  return value === true || value === 1 || value === '1' || value === 'true'
}

function normalizeMenuValue(value: unknown) {
  const raw = String(value ?? '').trim()
  const hashValue = raw.includes('#') ? raw.split('#').pop() : raw

  return String(hashValue ?? '')
    .toLowerCase()
    .trim()
    .replace(/^#/, '')
    .replace(/^\/+|\/+$/g, '')
    .replace(/^sections\//, '')
}

function resolveSectionId(menu: MenuItem): PortfolioSectionId | undefined {
  const candidates = [
    menu.target_type,
    menu.link,
    menu.name
  ].map(normalizeMenuValue)

  return defaultSections.find(section =>
    candidates.some(candidate =>
      candidate === section ||
      sectionAliases[section].includes(candidate) ||
      sectionAliases[section].some(alias => candidate.includes(alias))
    )
  )
}

function uniqueSections(sections: PortfolioSectionId[]) {
  return sections.filter((section, index) => sections.indexOf(section) === index)
}

export const usePortfolioStore = defineStore('portfolio', {
  state: () => ({
    // شکل پاسخ پروفایل بین endpointها یکسان نیست، پس رکورد باز نگه داشته می‌شود.
    profile: null as Record<string, any> | null,
    skills,
    services,
    // پروژه‌ها هیچ‌وقت مقدار نمونه نمی‌گیرند: بنر داده‌ی نمونه به
    // `/images/projects/*.png` اشاره می‌کرد که در `public/` وجود ندارد، پس هر
    // دامنه‌ای که پاسخ پروژه‌هایش خالی بود چند درخواست تصویرِ محکوم‌به‌شکست
    // به سرور خودش می‌زد (و محتوای نمونه‌ی یک نفر دیگر را هم نشان می‌داد).
    projects: [] as Project[],
    contacts: null as unknown[] | null,
    activeMenu: [] as MenuItem[],
    menuLoaded: false
  }),
  getters: {
    bySlug: (s) => (slug: string) =>
      s?.projects.find(p => p.slug === slug || String(p.id) === slug),
    featured: (s) => s?.projects.slice(0, 3),
    visibleSections: (s): PortfolioSectionId[] => {
      if (!s.menuLoaded) return defaultSections

      const sections = uniqueSections(
        s.activeMenu
          .map(resolveSectionId)
          .filter((section): section is PortfolioSectionId => Boolean(section))
      )

      return sections
    }
  },
  actions: {
    setMenu(menu: MenuItem[] | undefined) {
      if (!Array.isArray(menu)) {
        this.activeMenu = []
        this.menuLoaded = false
        return
      }

      this.activeMenu = menu.filter(item => isActive(item.active))
      this.menuLoaded = true
    }
  }
})
